import { ref } from 'vue'
import axios from 'axios'

// Create a small API client instance for session service
const createApiClient = () => {
  const getApiBaseUrl = () => {
    const isVercelDeployment = typeof window !== 'undefined' && window.location && 
      (window.location.hostname.includes('vercel.app') || window.location.hostname !== 'localhost')
    
    if (process.env.NODE_ENV === 'development' && !isVercelDeployment) {
      return process.env.VUE_APP_API_URL || 'http://localhost:3000'
    }
    
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin
    }
    
    return process.env.VUE_APP_API_URL || 'https://tierlist-app.vercel.app'
  }

  return axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.VUE_APP_API_KEY,
    },
  })
}

export function useSessionService() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * Get detailed session information including all albums with ratings
   * @param {string|number} sessionId - The session ID to fetch details for
   * @returns {Promise<Object>} Session with detailed album and rating data
   */
  const getSessionDetail = async (sessionId) => {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }

    loading.value = true
    error.value = null

    try {
      const apiClient = createApiClient()
      
      // Get the session with albums included
      const sessionResponse = await apiClient.get(`/api/sessions/${sessionId}?includeAlbums=true`)
      
      if (!sessionResponse.data || !sessionResponse.data.success) {
        throw new Error('Failed to fetch session data')
      }

      const session = sessionResponse.data.data
      
      if (!session) {
        throw new Error(`Session with ID ${sessionId} not found`)
      }

      // If no albums, return session as-is
      if (!session.albums || session.albums.length === 0) {
        return {
          ...session,
          albums: [],
          statistics: {
            totalAlbums: 0,
            avgRating: null,
            tierDistribution: {}
          }
        }
      }

      // Process albums to include individual ratings
      const albumsWithRatings = await Promise.all(
        session.albums.map(async (album) => {
          try {
            // Get individual ratings for each album
            const ratingsResponse = await apiClient.get(`/api/ratings/${album.id}`)
            const individualRatings = {}
            
            // Process ratings data - expecting array of rating objects
            if (ratingsResponse.data && ratingsResponse.data.success) {
              const ratings = ratingsResponse.data.data || []
              ratings.forEach(rating => {
                if (rating.reviewer_name && rating.score !== null) {
                  individualRatings[rating.reviewer_name] = rating.score
                }
              })
            }

            // Calculate average rating
            const ratingValues = Object.values(individualRatings).filter(r => r !== null && r !== undefined)
            const averageRating = ratingValues.length > 0 
              ? (ratingValues.reduce((sum, rating) => sum + rating, 0) / ratingValues.length).toFixed(1)
              : null

            return {
              ...album,
              individualRatings,
              averageRating
            }
          } catch (ratingsError) {
            console.warn(`Failed to fetch ratings for album ${album.id}:`, ratingsError)
            return {
              ...album,
              individualRatings: {},
              averageRating: null
            }
          }
        })
      )

      // Return enhanced session data
      const detailedSession = {
        ...session,
        albums: albumsWithRatings,
        statistics: {
          totalAlbums: albumsWithRatings.length,
          avgRating: calculateSessionAverageRating(albumsWithRatings),
          tierDistribution: calculateTierDistribution(albumsWithRatings)
        }
      }

      return detailedSession

    } catch (err) {
      error.value = err.message || 'Failed to load session details'
      console.error('Session detail loading error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Calculate session average rating across all albums
   * @param {Array} albums - Albums with individual ratings
   * @returns {number|null} Average rating or null if no ratings
   */
  const calculateSessionAverageRating = (albums) => {
    if (!albums || albums.length === 0) return null

    const albumAverages = albums
      .map(album => album.averageRating)
      .filter(avg => avg !== null && avg !== undefined)
      .map(avg => parseFloat(avg))

    if (albumAverages.length === 0) return null

    return (albumAverages.reduce((sum, avg) => sum + avg, 0) / albumAverages.length).toFixed(1)
  }

  /**
   * Calculate tier distribution for session albums
   * @param {Array} albums - Albums with tier assignments
   * @returns {Object} Tier distribution object
   */
  const calculateTierDistribution = (albums) => {
    if (!albums || albums.length === 0) return {}

    const distribution = {}
    albums.forEach(album => {
      const tier = album.tier || 'Not Rated Yet'
      distribution[tier] = (distribution[tier] || 0) + 1
    })

    return distribution
  }

  return {
    loading,
    error,
    getSessionDetail,
    calculateSessionAverageRating,
    calculateTierDistribution
  }
}